import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Abertura animada do modulo de Checklist -- efeito de galaxia/cosmos em
// canvas puro (sem libs externas, pra nao pesar o bundle so por causa de
// um efeito decorativo). Toca uma unica vez por entrada na rota /checklist
// (ver ChecklistLicenseGate, que monta este componente e o remove quando
// onFinish e chamado). Duracao curta de proposito -- e so uma "vinheta" de
// abertura, nao pode atrapalhar quem usa o checklist va+rias vezes ao dia
// em campo. Respeita prefers-reduced-motion (pula direto, sem animar).
const DURACAO_MS = 2200;
const FADE_MS = 480;

export default function CosmicIntro({ onFinish }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const reduzida = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduzida) {
      onFinish();
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf = null;
    let start = null;
    let ativo = true;

    function resize() {
      canvas.width = window.innerWidth * (window.devicePixelRatio || 1);
      canvas.height = window.innerHeight * (window.devicePixelRatio || 1);
    }
    resize();
    window.addEventListener("resize", resize);
    ctx.fillStyle = "#040614";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Particulas distribuidas em bracos espirais, mais densas perto do
    // nucleo -- mesma ideia visual da referencia (galaxia azul girando
    // sobre fundo escuro), so que animada em vez de estatica.
    const BRACOS = 3;
    const N = 260;
    const particulas = Array.from({ length: N }, (_, i) => {
      const arm = i % BRACOS;
      const r = Math.pow(Math.random(), 1.6);
      const anguloBase = (arm / BRACOS) * Math.PI * 2 + r * 5.5 + (Math.random() - 0.5) * 0.6;
      return {
        r,
        anguloBase,
        tamanho: 0.6 + Math.random() * 1.8,
        brilho: 0.4 + Math.random() * 0.6,
        fase: Math.random() * Math.PI * 2,
        vel: 0.4 + Math.random() * 0.3,
      };
    });

    function frame(ts) {
      if (start === null) start = ts;
      const decorrido = ts - start;
      const w = canvas.width, h = canvas.height;
      const cx = w / 2, cy = h / 2;
      const maxR = Math.min(w, h) * 0.46;

      // rastro suave em vez de limpar tudo -- da uma sensacao de
      // profundidade/movimento continuo, como um leve motion blur.
      ctx.fillStyle = "rgba(4,6,20,0.35)";
      ctx.fillRect(0, 0, w, h);

      const progresso = Math.min(1, decorrido / (DURACAO_MS * 0.7));
      const escala = 0.82 + progresso * 0.28;

      const nucleoGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR * 0.35 * escala);
      nucleoGrad.addColorStop(0, "rgba(150,210,255,0.55)");
      nucleoGrad.addColorStop(0.4, "rgba(60,120,255,0.22)");
      nucleoGrad.addColorStop(1, "rgba(10,20,60,0)");
      ctx.fillStyle = nucleoGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, maxR * 0.6 * escala, 0, Math.PI * 2);
      ctx.fill();

      const rotacao = decorrido * 0.00035;
      particulas.forEach((p) => {
        const ang = p.anguloBase + rotacao * p.vel;
        const raio = p.r * maxR * escala;
        const x = cx + Math.cos(ang) * raio;
        const y = cy + Math.sin(ang) * raio * 0.62;
        const brilhoTremido = p.brilho * (0.7 + 0.3 * Math.sin(decorrido * 0.004 + p.fase));
        const tam = p.tamanho * (window.devicePixelRatio || 1) * (0.6 + progresso * 0.6);
        ctx.beginPath();
        ctx.fillStyle = `rgba(${190 + Math.round(brilhoTremido * 50)},215,255,${brilhoTremido.toFixed(2)})`;
        ctx.arc(x, y, tam, 0, Math.PI * 2);
        ctx.fill();
      });

      if (decorrido < DURACAO_MS && ativo) {
        raf = requestAnimationFrame(frame);
      }
    }
    raf = requestAnimationFrame(frame);

    const fadeTimer = setTimeout(() => setFading(true), DURACAO_MS - FADE_MS);
    const endTimer = setTimeout(() => { ativo = false; onFinish(); }, DURACAO_MS + 60);

    return () => {
      ativo = false;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      clearTimeout(fadeTimer);
      clearTimeout(endTimer);
    };
  }, []);

  function pular() {
    setFading(true);
    setTimeout(onFinish, 220);
  }

  return (
    <div
      onClick={pular}
      role="button"
      aria-label={t("checklist.intro_pular")}
      style={{
        position: "fixed", inset: 0, zIndex: 20000, background: "#040614",
        cursor: "pointer", opacity: fading ? 0 : 1, transition: `opacity ${FADE_MS}ms ease`,
      }}
    >
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 34, textAlign: "center" }}>
        <div style={{ color: "rgba(210,225,255,0.85)", fontSize: 13, fontWeight: 600, letterSpacing: "0.08em" }}>
          {t("checklist.intro_abrindo")}
        </div>
        <div style={{ color: "rgba(210,225,255,0.4)", fontSize: 11, marginTop: 6 }}>
          {t("checklist.intro_pular")}
        </div>
      </div>
    </div>
  );
}
